<template>
  <div class="mural-card">
    <div class="flex items-center justify-between mb-1" style="gap: 1rem;">
      <h4 class="mb-0 mural-side-title">{{ $t('mural.upcoming.title') }}</h4>
      <a class="mural-link" @click="$emit('go-agenda')">{{ $t('mural.upcoming.see_agenda') }}</a>
    </div>

    <div class="flex flex-column" style="gap: .9rem;">
      <div
        v-for="item in items"
        :key="item.key"
        class="mural-upcoming"
        @click="$emit('select', item.payload)">
        <div class="mural-upcoming__when">
          <div class="mural-upcoming__time">{{ item.startTime }}</div>
          <div class="mural-upcoming__day">{{ item.dayLabel }}</div>
        </div>
        <span class="mural-upcoming__bar" :style="{ background: item.color }"></span>
        <div class="mural-upcoming__main">
          <div class="mural-upcoming__title">{{ item.name }}</div>
          <div class="mural-upcoming__sub">{{ item.subtitle }}</div>
        </div>
        <div class="mural-upcoming__occ">
          <div class="font-weight-bold">{{ item.occupancy }}</div>
          <div class="mural-upcoming__track">
            <div :style="{ width: `${item.percent}%`, background: item.color }"></div>
          </div>
        </div>
      </div>

      <div v-if="!items.length" class="text-center text-muted py-3">
        <small>{{ $t('mural.upcoming.empty') }}</small>
      </div>
    </div>
  </div>
</template>

<script>
import {
  WEEKDAY_SHORT,
  isSameDay,
  occupancyLabel,
  occupancyPercent,
  parseDate,
  slotStatus,
} from './muralAgenda'

/**
 * As próximas cinco aulas a partir de agora.
 *
 * Sai da mesma janela que alimenta a grade — não é uma segunda consulta. Por
 * isso a lista cobre só o que estiver carregado: navegar a agenda para outro mês
 * não muda o que aparece aqui, e é o comportamento certo (é "próximas", não "as
 * do período visível").
 */
export default {
  name: 'MuralUpcoming',
  props: {
    days: { type: Array, default: () => [] },
    classes: { type: Array, default: () => [] },
    limit: { type: Number, default: 5 },
  },
  computed: {
    classById() {
      return this.classes.reduce((acc, item) => {
        acc[item.id] = item
        return acc
      }, {})
    },
    items() {
      const now = new Date()
      const today = new Date()
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)

      const result = []

      this.days.forEach(day => {
        const date = parseDate(day.date)

        ;(day.slots || []).forEach(slot => {
          if (result.length >= this.limit) return

          const status = slotStatus(slot)
          if (status === 'cancelled' || status === 'blocked') return

          const [hour, minute] = slot.startTime.split(':').map(Number)
          const startsAt = new Date(date)
          startsAt.setHours(hour, minute || 0, 0, 0)
          if (startsAt.getTime() < now.getTime()) return

          const lesson = this.classById[slot.lessonId] || {}
          const teacher = (lesson.teachers || [])[0]

          let dayLabel = `${WEEKDAY_SHORT[(date.getDay() + 6) % 7]} ${date.getDate()}`
          if (isSameDay(date, today)) dayLabel = 'Hoje'
          else if (isSameDay(date, tomorrow)) dayLabel = 'Amanhã'

          result.push({
            key: `${slot.lessonId}|${day.date}|${slot.startTime}`,
            startTime: slot.startTime,
            dayLabel,
            name: lesson.name || '',
            subtitle: teacher ? teacher.name : (lesson.modality_name || ''),
            color: lesson.color || '#8BB2DB',
            occupancy: occupancyLabel(slot),
            percent: occupancyPercent(slot),
            payload: { date: day.date, slot, lesson },
          })
        })
      })

      return result
    },
  },
}
</script>

<style lang="scss" scoped>
.mural-card {
  background: var(--surface-card);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-card);
  padding: 1.25rem;
}

.mural-side-title {
  font-size: 1.07rem;
  font-weight: var(--fw-semibold);
  color: var(--text-heading);
}

.mural-link {
  font-size: 0.8rem;
  cursor: pointer;
  white-space: nowrap;
  color: var(--primary);
}

.mural-upcoming {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  border-radius: var(--radius);
  padding: 0.2rem 0.25rem;

  &:hover {
    background: var(--surface-hover);
  }

  &__when {
    flex: none;
    width: 52px;
    text-align: center;
  }

  &__time {
    font-size: 0.92rem;
    font-weight: var(--fw-semibold);
    color: var(--text-heading);
  }

  &__day {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--text-muted);
  }

  &__bar {
    width: 3px;
    align-self: stretch;
    border-radius: var(--radius-pill);
    flex: none;
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.875rem;
    font-weight: var(--fw-semibold);
    color: var(--text-heading);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__sub {
    font-size: 0.75rem;
    color: var(--text-body);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__occ {
    flex: none;
    width: 56px;
    text-align: right;
    font-size: 0.8rem;
  }

  &__track {
    height: 4px;
    border-radius: var(--radius-pill);
    background: rgba(0, 0, 0, 0.08);
    margin-top: 4px;

    > div {
      height: 100%;
      border-radius: var(--radius-pill);
    }
  }
}
</style>
